import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import ReactMarkdown from "react-markdown";
import { Save, Sparkles, ArrowLeft, Eye, AlertCircle, Wand2, Send } from "lucide-react";
import { LOGO_URL } from "@/lib/siteData";

const CATEGORIES = ["Crédit", "Placement", "Assurance", "Professionnels", "Mentoring", "Actualité"];
const toLocalInput = (iso) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};


export default function ArticleEditor() {
  const { id } = useParams();
  const isNew = !id;
  const { authFetch } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState({
    title: "",
    category: "Crédit",
    excerpt: "",
    content: "",
    image: "",
    status: "draft",
    scheduled_at: "",
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(false);

  // AI generation
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTheme, setAiTheme] = useState("");
  const [aiCategory, setAiCategory] = useState("Crédit");
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const res = await authFetch(`/admin/articles/${id}`);
        const doc = await res.json();
        setData({
          title: doc.title || "",
          category: doc.category || "Crédit",
          excerpt: doc.excerpt || "",
          content: doc.content || "",
          image: doc.image || "",
          status: doc.status || "draft",
          scheduled_at: doc.scheduled_at ? toLocalInput(doc.scheduled_at) : "",
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew, authFetch]);

  const save = async (publish = null) => {
    setError("");
    // Determine target status based on button + scheduled_at
    let status;
    if (publish === false) {
      status = "draft"; // "Enregistrer brouillon"
    } else if (data.scheduled_at) {
      status = "scheduled"; // Date future saisie → toujours programmer, jamais publier immédiatement
    } else if (publish === true) {
      status = "published";
    } else {
      status = data.status;
    }
    const payload = { ...data, status };
    if (status === "scheduled") {
      const dt = new Date(data.scheduled_at);
      if (isNaN(dt.getTime()) || dt.getTime() <= Date.now()) {
        setError("La date de publication programmée doit être dans le futur.");
        return;
      }
      payload.scheduled_at = dt.toISOString();
    } else {
      payload.scheduled_at = null;
    }
    if (!payload.title.trim() || !payload.excerpt.trim() || !payload.content.trim()) {
      setError("Le titre, l'accroche et le contenu sont obligatoires.");
      return;
    }
    setSaving(true);
    try {
      const url = isNew ? "/admin/articles" : `/admin/articles/${id}`;
      const method = isNew ? "POST" : "PUT";
      const res = await authFetch(url, { method, body: JSON.stringify(payload) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur d'enregistrement");
      }
      const saved = await res.json();
      navigate("/admin");
      return saved;
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const generateAi = async () => {
    if (!aiTheme.trim()) return;
    setAiLoading(true);
    setError("");
    try {
      const res = await authFetch("/admin/articles/generate", {
        method: "POST",
        body: JSON.stringify({ theme: aiTheme, category: aiCategory }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Génération échouée");
      }
      const out = await res.json();
      setData((d) => ({
        ...d,
        title: out.title || d.title,
        excerpt: out.excerpt || d.excerpt,
        content: out.content || d.content,
        category: out.category || d.category,
      }));
      setAiOpen(false);
      setAiTheme("");
    } catch (e) {
      setError(e.message);
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-md-muted">Chargement…</div>;

  return (
    <div className="min-h-screen bg-md-bg">
      <header className="bg-white border-b border-md-border sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/admin" className="flex items-center gap-3 text-md-muted hover:text-md-primary">
            <ArrowLeft className="w-4 h-4" />
            <img src={LOGO_URL} alt="MD" className="h-10 object-contain" />
            <span className="text-xs uppercase tracking-widest font-bold border-l border-md-border pl-3">
              {isNew ? "Nouvel article" : "Édition"}
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              data-testid="toggle-preview"
              onClick={() => setPreview((p) => !p)}
              className="inline-flex items-center gap-1.5 text-sm border border-md-border bg-white text-md-primary px-4 h-10 rounded-full hover:border-md-secondary"
            >
              <Eye className="w-4 h-4" /> {preview ? "Édition" : "Aperçu"}
            </button>
            <button
              data-testid="save-draft-btn"
              onClick={() => save(false)}
              disabled={saving}
              className="inline-flex items-center gap-1.5 text-sm border border-md-border bg-white text-md-primary px-4 h-10 rounded-full hover:border-md-secondary disabled:opacity-50"
            >
              <Save className="w-4 h-4" /> Enregistrer brouillon
            </button>
            <button
              data-testid="publish-btn"
              onClick={() => save(true)}
              disabled={saving}
              className="inline-flex items-center gap-1.5 text-sm bg-md-secondary hover:bg-md-secondaryHover text-white px-5 h-10 rounded-full font-semibold disabled:opacity-50"
            >
              <Send className="w-4 h-4" /> {saving ? "…" : (data.scheduled_at ? "Programmer" : "Publier")}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {error && (
          <div data-testid="editor-error" className="mb-6 flex items-start gap-2 p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl">
            <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{error}</span>
          </div>
        )}

        {/* AI panel */}
        <div className="mb-8 bg-md-primary text-white rounded-3xl overflow-hidden">
          <button
            type="button"
            data-testid="ai-panel-toggle"
            onClick={() => setAiOpen((o) => !o)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-md-primaryHover transition-colors"
          >
            <span className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-md-secondary" />
              <span className="font-semibold">Générer un brouillon avec l'IA</span>
              <span className="text-xs text-white/60 hidden sm:inline">— gagnez 10 min, écrivez juste votre relecture</span>
            </span>
            <span className="text-white/60 text-sm">{aiOpen ? "−" : "+"}</span>
          </button>
          {aiOpen && (
            <div className="px-6 pb-6 grid md:grid-cols-12 gap-3">
              <div className="md:col-span-7">
                <label className="text-[10px] uppercase tracking-widest text-md-secondary block mb-1.5">Sujet de l'article</label>
                <input
                  data-testid="ai-theme-input"
                  value={aiTheme}
                  onChange={(e) => setAiTheme(e.target.value)}
                  placeholder="Ex : La nouvelle loi Lemoine et son impact sur l'assurance emprunteur en 2026"
                  className="w-full bg-white/10 border border-white/20 rounded-xl p-3 text-white placeholder:text-white/40 focus:outline-none focus:border-md-secondary"
                />
              </div>
              <div className="md:col-span-3">
                <label className="text-[10px] uppercase tracking-widest text-md-secondary block mb-1.5">Catégorie</label>
                <select
                  data-testid="ai-category"
                  value={aiCategory}
                  onChange={(e) => setAiCategory(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-xl p-3 text-white focus:outline-none focus:border-md-secondary"
                >
                  {CATEGORIES.map((c) => <option key={c} value={c} className="bg-md-primary">{c}</option>)}
                </select>
              </div>
              <div className="md:col-span-2 flex items-end">
                <button
                  data-testid="ai-generate-btn"
                  onClick={generateAi}
                  disabled={aiLoading || !aiTheme.trim()}
                  className="w-full bg-md-secondary hover:bg-md-secondaryHover disabled:opacity-50 text-white px-4 h-12 rounded-xl font-semibold inline-flex items-center justify-center gap-1.5"
                >
                  <Wand2 className="w-4 h-4" /> {aiLoading ? "…" : "Générer"}
                </button>
              </div>
              <p className="md:col-span-12 text-xs text-white/60 leading-relaxed">
                💡 Astuce : soyez spécifique (date, public ciblé, angle). L'IA produit un article de 800-1100 mots que vous pourrez ensuite relire et ajuster.
              </p>
            </div>
          )}
        </div>

        {!preview ? (
          <div className="space-y-5">
            <Field label="Titre">
              <input
                data-testid="editor-title"
                value={data.title}
                onChange={(e) => setData({ ...data, title: e.target.value })}
                placeholder="Un titre clair, accrocheur (max 90 caractères)"
                className="w-full bg-white border border-md-border rounded-xl p-4 text-xl font-serif text-md-primary focus:outline-none focus:border-md-secondary"
              />
            </Field>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Catégorie">
                <select
                  data-testid="editor-category"
                  value={data.category}
                  onChange={(e) => setData({ ...data, category: e.target.value })}
                  className="w-full bg-white border border-md-border rounded-xl p-3.5 text-md-text focus:outline-none focus:border-md-secondary"
                >
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Image de couverture (URL)">
                <input
                  data-testid="editor-image"
                  value={data.image}
                  onChange={(e) => setData({ ...data, image: e.target.value })}
                  placeholder="https://… (laissez vide pour image par défaut)"
                  className="w-full bg-white border border-md-border rounded-xl p-3.5 text-md-text focus:outline-none focus:border-md-secondary"
                />
              </Field>
            </div>
            <Field label="📅 Publication programmée (optionnel)">
              <input
                data-testid="editor-scheduled-at"
                type="datetime-local"
                value={data.scheduled_at}
                onChange={(e) => setData({ ...data, scheduled_at: e.target.value })}
                className="w-full bg-white border border-md-border rounded-xl p-3.5 text-md-text focus:outline-none focus:border-md-secondary"
              />
              <div className="text-[11px] text-md-muted mt-1.5">
                Laissez vide pour publier immédiatement. Sinon, l'article sera automatiquement publié à la date choisie (contrôlé toutes les 15 min).
              </div>
            </Field>
            <Field label="Accroche (excerpt)">
              <textarea
                data-testid="editor-excerpt"
                rows={2}
                value={data.excerpt}
                onChange={(e) => setData({ ...data, excerpt: e.target.value })}
                placeholder="180-260 caractères — c'est ce qu'on lit sur la page d'accueil"
                className="w-full bg-white border border-md-border rounded-xl p-4 text-md-text focus:outline-none focus:border-md-secondary"
              />
              <div className="text-[10px] text-right text-md-muted mt-1">{data.excerpt.length} caractères</div>
            </Field>
            <Field label="Contenu (Markdown)">
              <textarea
                data-testid="editor-content"
                rows={22}
                value={data.content}
                onChange={(e) => setData({ ...data, content: e.target.value })}
                placeholder="Écrivez en markdown. Sous-titres = ## Titre. Listes = - élément. Gras = **mot**."
                className="w-full bg-white border border-md-border rounded-xl p-4 text-md-text font-mono text-sm leading-relaxed focus:outline-none focus:border-md-secondary"
                style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
              />
            </Field>
          </div>
        ) : (
          <article className="bg-white border border-md-border rounded-3xl p-10 max-w-3xl mx-auto" data-testid="editor-preview">
            {data.image && <img src={data.image} alt="" className="w-full h-72 object-cover rounded-2xl mb-8" />}
            <div className="text-[10px] uppercase tracking-widest font-bold text-md-secondary mb-3">{data.category}</div>
            <h1 className="font-serif text-4xl text-md-primary font-medium mb-4">{data.title || "Sans titre"}</h1>
            <p className="text-lg text-md-muted mb-8 italic">{data.excerpt}</p>
            <div className="prose-md">
              <ReactMarkdown>{data.content || "_Aucun contenu pour le moment._"}</ReactMarkdown>
            </div>
          </article>
        )}
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-md-accent mb-2 block">{label}</span>
      {children}
    </label>
  );
}
